import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DropAnimation,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDropzone } from 'react-dropzone'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { MyImage } from 'components/MyImage'
import { cdnUrl } from 'commons/cdn'
import { X } from 'lucide-react'
import { uploadImages } from './api'

interface Props {
  value: string[]
  onChange: (urls: string[]) => void
  accept?: string
  disabled?: boolean
  label?: string
}

type ImageItem = { id: string; url: string }

let idSeq = 0
const nextId = () => `img-${Date.now().toString(36)}-${++idSeq}`

/** 按 url 顺序尽量复用稳定 id，避免拖拽重排时节点乱重建 */
function useStableImageItems(urls: string[]): ImageItem[] {
  const prevRef = useRef<ImageItem[]>([])
  return useMemo(() => {
    const prev = prevRef.current
    const used = new Set<number>()
    const next = urls.map((url) => {
      const found = prev.findIndex((p, i) => p.url === url && !used.has(i))
      if (found >= 0) {
        used.add(found)
        return prev[found]
      }
      return { id: nextId(), url }
    })
    prevRef.current = next
    return next
  }, [urls])
}

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
}

const SortableImageItem: React.FC<{
  item: ImageItem
  index: number
  disabled?: boolean
  onRemove: () => void
}> = ({ item, index, disabled, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative h-32 w-32 touch-none overflow-hidden rounded-md border border-border bg-background"
      {...(disabled ? {} : { ...attributes, ...listeners })}
    >
      <MyImage
        src={cdnUrl(item.url)}
        alt={`参考图 ${index + 1}`}
        className="h-full w-full object-cover"
        lazy={false}
        preview={!disabled && !isDragging}
      />
      {!disabled ? (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="absolute right-0.5 top-0.5 z-10 rounded-full bg-black/60 p-0.5 text-white"
          title="删除"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}

export const MultiImageUpload: React.FC<Props> = ({
  value,
  onChange,
  accept = 'image/*',
  disabled,
  label,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const items = useStableImageItems(value)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // 移动超过 8px 才开始拖，短按仍可点图预览
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const activeItem = activeId
    ? items.find((it) => it.id === activeId) || null
    : null

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return
      try {
        setUploading(true)
        const urls = await uploadImages(files)
        onChange([...value, ...urls])
        toast({
          title: '上传成功',
          description: `${urls.length} 个文件`,
          className: 'bg-green-500 text-white',
        })
      } catch (err: any) {
        toast({
          variant: 'destructive',
          title: '上传失败',
          description: err?.message || '未知错误',
        })
      } finally {
        setUploading(false)
        if (inputRef.current) inputRef.current.value = ''
      }
    },
    [onChange, value],
  )

  const onSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    await uploadFiles(Array.from(files))
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: true,
    disabled: disabled || uploading,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted) => {
      void uploadFiles(accepted)
    },
    onDropRejected: () => {
      toast({
        variant: 'destructive',
        title: '请拖拽图片文件',
      })
    },
  })

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    if (disabled) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((it) => it.id === String(active.id))
    const newIndex = items.findIndex((it) => it.id === String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(value, oldIndex, newIndex))
  }

  const onDragCancel = () => setActiveId(null)

  return (
    <div className="space-y-2">
      {label ? <Label>{label}</Label> : null}

      <div className="flex flex-wrap items-center gap-2">
        <div
          {...getRootProps()}
          className={cn(
            'flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3',
            isDragActive && 'border-primary bg-primary/5',
            (disabled || uploading) && 'opacity-60',
          )}
        >
          <input {...getInputProps()} />
          <Input
            ref={inputRef}
            type="file"
            multiple
            accept={accept}
            onChange={onSelect}
            disabled={disabled || uploading}
            className="h-auto w-auto border-0 p-0 shadow-none focus-visible:ring-0 file:mr-2 file:py-0 file:leading-none"
          />
          <span className="shrink-0 text-xs leading-none text-muted-foreground">
            {isDragActive ? '松开上传' : '可拖拽上传'}
          </span>
        </div>
        {uploading ? (
          <span className="text-xs text-muted-foreground">上传中...</span>
        ) : null}
        {!disabled && value.length > 1 ? (
          <span className="text-xs text-muted-foreground">
            按住拖拽可调整顺序，点击可预览
          </span>
        ) : null}
      </div>

      {items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext
            items={items.map((it) => it.id)}
            strategy={rectSortingStrategy}
          >
            <div className="flex flex-wrap gap-3">
              {items.map((item, i) => (
                <SortableImageItem
                  key={item.id}
                  item={item}
                  index={i}
                  disabled={disabled}
                  onRemove={() =>
                    onChange(value.filter((_, idx) => idx !== i))
                  }
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimation}>
            {activeItem ? (
              <div className="h-32 w-32 cursor-grabbing overflow-hidden rounded-md border border-border shadow-lg">
                <img
                  src={cdnUrl(activeItem.url)}
                  alt=""
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : null}
    </div>
  )
}

export default MultiImageUpload
